import { useAuthStore } from '@/stores/authStore';
import i18n from '@/i18n';

const t = (key, params) => i18n.global.t(key, params);
import { defineStore } from 'pinia';
import axios from '@/services/axios';
import ApiClient from '@/services/apiClient';
import Facility from '@/models/facility';
import Installer from '@/models/installer';
import Hub from '@/models/hub';
import MfaInfo from '@/models/mfaInfo';
import Intervention from '@/models/intervention';
import Scenario from '@/models/scenario';
import Alert from '@/models/alert';
import Member from '@/models/member.js';
import ExternalAlarm from '@/models/externalAlarm';
import CardProfile from '@/models/cardProfile';
import License from '@/models/license';
import { VideoDevice, NVR, Camera, ProbeResult, DiscoveredCamera } from '@/models/video';

// Error message of an AJAX or SPC configuration, 412 meaning the LINK is not connected (as in the app)
function integrationErrorMessage(error) {
  if (error.response && error.response.status === 412) {
    return t('api.linkNotConnected');
  }
  let message = t('common.updateFailure');
  if (error.response) message += ` (${error.response.status})`;
  return message;
}

// The backend waits up to 45 s (probe) and 20 s (discovery) for the LINK: longer than the default axios timeout
const VIDEO_PROBE_TIMEOUT = 60000;
const VIDEO_DISCOVER_TIMEOUT = 30000;

function videoBasePath(facilityId, hub) {
  return `/conf/facilities/${facilityId}/components/${hub.serial_number}`;
}

// The backend waits up to 10 s for the LINK when pairing or unpairing a UZ product, about 45 s when discovering Aperio locks
const MECA_TIMEOUT = 30000;
const APERIO_DISCOVER_TIMEOUT = 60000;

function componentPath(facilityId, hub) {
  return `/conf/facilities/${facilityId}/components/${hub.serial_number}`;
}

export const useUserStore = defineStore('user', {
  state: () => ({
    installer: null,
    facilities: [],
    facilitiesLoaded: false,
    mfa_info: null,
    timezones: [],
    supportUnread: 0,
    supportPanelRequested: false,
    availableLicenses: null,
  }),

  actions: {
    // Login function using Axios
    async login(email, password, cguAccepted) {
      try {
        const credentials = {
          email: email,
          password: password
        };
        if (cguAccepted) {
          credentials.cgu_accepted = cguAccepted;
        }
        let url = '/conf/login';
        console.log("theme", process.env.VUE_APP_SEND_LOGIN_THEME);
        if (process.env.VUE_APP_SEND_LOGIN_THEME === 'true') {
          const theme = window.location.hostname.split(".")[0];
          url += `?theme=${theme}`;
        }
        const response = await axios.post(url, credentials);
        this.setTokenExpiryDate(response.data.expiry, response.data.max_renew);
      } catch (error) {
        if (error.response) {
          switch (error.response.status) {
            case 400:
              throw 'LoginFailed';
            case 401:
              window.location = ApiClient.convertToType(error.response.data, 'String');
              throw 'Redirect';
            case 410:
              throw "AccountDeletionInProgress";
            case 412:
              throw "AccountNotActivated";
            case 428:
              this.mfa_info = ApiClient.convertToType(error.response.data.mfa_info, MfaInfo);
              // Approval on the phone: no code to type, the page waits for it.
              return this.mfa_info.method === 'push' ? '/approve' : '/mfa';
            default:
              throw `${t('api.unexpectedError')} (${error.response.status})`;
          }
        }
        throw error;
      }
      return await this.loadInstaller();
    },

    // MFA Login function using Axios. `code`: the code received, or the id of
    // the sign-in request approved on a phone.
    async mfaLogin(code) {
      try {
        const response = await axios.post('/conf/mfa/web', { code });
        this.setTokenExpiryDate(response.data.expiry, response.data.max_renew);
      } catch (error) {
        console.error('MFA login failed', error);
        throw t('api.invalidCode');
      }

      return await this.loadInstaller();
    },

    // Sign-in approval on a phone: where the request stands
    // (pending | approved | denied | expired | consumed).
    async loginChallengeStatus(id) {
      const response = await axios.get(`/conf/login_challenges/${id}`);
      return response.data.status;
    },

    // The phone is out of reach: a code goes by email and the code form
    // takes over.
    async loginChallengeFallback(id) {
      const response = await axios.post(`/conf/login_challenges/${id}/fallback`);
      this.mfa_info = ApiClient.convertToType(response.data, MfaInfo);
    },

    // Phones enrolled (from the Haxo Setup app) to approve the current
    // installer's sign-ins.
    async loadAuthenticators() {
      try {
        const response = await axios.get('/conf/authenticators');
        return response.data;
      } catch (error) {
        console.error('loadAuthenticators', error);
        return [];
      }
    },

    // 'ok', 'last' (the only phone of an account protected by the approval
    // on a phone: the method must change first), 'error', or {result:
    // 'need_approval', mfa} when the account's phone must approve the
    // removal: called again with the approved request's id as [code].
    async removeAuthenticator(id, code) {
      try {
        await axios.delete(`/conf/authenticators/${id}`, { params: code ? { code } : {} });
        return 'ok';
      } catch (error) {
        // The phones are the account's second factor: the server opened a
        // request the phone must approve instead of removing anything.
        if (error.response?.status === 428) {
          return { result: 'need_approval', mfa: ApiClient.convertToType(error.response.data.mfa_info, MfaInfo) };
        }
        console.error('removeAuthenticator', error);
        return error.response?.status === 409 ? 'last' : 'error';
      }
    },

    // Logout function using Axios
    async logout() {
      try {
        await axios.post('/conf/logout');
      } catch (error) {
        console.error('Logout failed', error);
      }
      this.clearToken();
      this.installer = null;
      this.facilities = [];
      this.facilitiesLoaded = false;
    },

    setTokenExpiryDate(expiryDate, maxRenew) {
      const auth = useAuthStore();
      auth.handleLoginSuccess(expiryDate, maxRenew);
    },

    clearToken() {
      const auth = useAuthStore();
      auth.clearAuthState();
    },

    // Resend activation link
    async resendActivationLink(email) {
      try {
        await axios.post('/conf/installers/resend', { email: email });
        return true;
      } catch (error) {
        console.error('resendActivationLink', error);
        return false;
      }
    },

    // Send recovery password link
    async sendRecoveryPasswordLink(email) {
      try {
        await axios.post('/conf/installers/recoverypwd', { email: email });
        return true;
      } catch (error) {
        console.error('sendRecoveryPasswordLink', error);
        return false;
      }
    },

    // Change password
    async changePassword(uuid, password, type) {
      try {
        if (!uuid || uuid.length === 0) {
          throw "empty uuid";
        }
        if (!type || type.length === 0) {
          throw "empty type";
        }
        const body = {
          uuid: uuid,
          password: password
        }
        const response = await axios.post(`/conf/${type}/changepwd`, body);
        this.mfa_info = ApiClient.convertToType(response.data.mfa_info, MfaInfo);
        if (this.mfa_info) {
          return 'mfaNeeded';
        } else {
          return 'ok';
        }
      } catch (error) {
        console.error('changePasswordLink', error);
        throw t('api.changePasswordFailed');
      }
    },

    // Change password MFA. type comes from the reset link: "installers", or
    // "users" for the Haxo Open accounts.
    async mfaChangePassword(uuid, code, type) {
      try {
        await axios.post(`/conf/${type || 'installers'}/changepwd/mfa`, { uuid: uuid, code: code });
      } catch (error) {
        console.error('mfaChangePassword', error);
        if (error.response && error.response.status == 404) {
          throw t('api.invalidLink');
        }
        if (error.response && error.response.status == 400) {
          throw t('api.invalidCode');
        }
        throw t('api.unexpectedError');
      }
    },

    // Load installer information, facilities and timezones
    async loadInstaller() {

      await this.loadInstallerInformation();

      await this.loadInstallerFacilities();

      await this.loadTimezones();

      return '/installation-list';
    },

    async loadInstallerInformation() {
      try {
        const response = await axios.get('/conf/installers/me');
        this.installer = ApiClient.convertToType(response.data, Installer);
      } catch (error) {
        console.error("Load installer information", error);
        let message = "Erreur lors du chargement de votre compte";
        if (error.response) message += ` (${error.response.status})`
        throw message;
      }

      return await this.loadInstallerFacilities();
    },

    // Load installer facilities
    async loadInstallerFacilities() {
      try {
        const response = await axios.get('/conf/installers/me/facilities');
        const facilities = ApiClient.convertToType(response.data, [Facility]);
        // Une nouvelle facility n'est pas couverte par le token courant : renew nécessaire
        const knownIds = new Set(this.facilities.map(f => f.id));
        const hasNewFacilities = this.facilitiesLoaded && facilities.some(f => !knownIds.has(f.id));
        this.facilities = facilities;
        this.facilitiesLoaded = true;
        if (hasNewFacilities) {
          await useAuthStore().refreshToken();
        }
      } catch (error) {
        console.error("Load installer facilities", error);
        let message = "Erreur lors du chargement de vos installations";
        if (error.response) message += ` (${error.response.status})`
        throw message;
      }
    },

    // Load time zones
    async loadTimezones() {
      try {
        const response = await axios.get('/conf/timezones');
        this.timezones = ApiClient.convertToType(response.data.timezones, ['String']);
      } catch (error) {
        console.error("Load timezones", error);
        let message = "Erreur lors du chargement des fuseaux horaires";
        if (error.response) message += ` (${error.response.status})`
        throw message;
      }
    },

    // Load facility components
    async loadComponents(facilityId) {
      try {
        const facility = this.getFacility(facilityId);
        const response = await axios.get(`/conf/facilities/${facilityId}`);
        // Load hubs
        facility.hubs = ApiClient.convertToType(response.data.hubs, [Hub]);
        // Update components
        facility.updateComponents();
      } catch (error) {
        console.error('Components loading failed', error);
      }
    },

    // Load facility external alarms
    async loadExternalAlarms(facilityId) {
      try {
        const facility = this.getFacility(facilityId);
        const response = await axios.get(`/conf/facilities/${facilityId}/external_alarms`);
        // Load external alarms
        facility.external_alarms = ApiClient.convertToType(response.data, [ExternalAlarm]);
      } catch (error) {
        console.error('External alarms loading failed', error);
      }
    },

    // Load facility card profiles
    async loadCardProfiles(facilityId) {
      try {
        const facility = this.getFacility(facilityId);
        const response = await axios.get(`/conf/facilities/${facilityId}/card_profiles`);
        facility.card_profiles = ApiClient.convertToType(response.data, [CardProfile]) || [];
        return facility.card_profiles;
      } catch (error) {
        console.error('Card profiles loading failed', error);
      }
    },

    getCardProfiles(facilityId) {
      const facility = this.getFacility(facilityId);
      return facility.card_profiles || [];
    },

    async createCardProfile(facilityId, profile) {
      try {
        const response = await axios.post(`/conf/facilities/${facilityId}/card_profiles`, profile);
        const createdProfile = response.data ? ApiClient.convertToType(response.data, CardProfile) : profile;
        this.getFacility(facilityId).card_profiles.push(createdProfile);
        return createdProfile;
      } catch (error) {
        console.error('createCardProfile failed', error);
        let message = t('api.addBadgeFormatFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    async updateCardProfile(facilityId, profile) {
      try {
        const response = await axios.put(`/conf/facilities/${facilityId}/card_profiles/${profile.id}`, profile);
        const updatedProfile = response.data ? ApiClient.convertToType(response.data, CardProfile) : profile;
        const profiles = this.getFacility(facilityId).card_profiles;
        const index = profiles.findIndex(item => item.id === profile.id);
        if(index !== -1) {
          profiles[index] = updatedProfile;
        }
        return updatedProfile;
      } catch (error) {
        console.error('updateCardProfile failed', error);
        let message = t('api.updateBadgeFormatFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    async deleteCardProfile(facilityId, profile) {
      try {
        await axios.delete(`/conf/facilities/${facilityId}/card_profiles/${profile.id}`);
        const profiles = this.getFacility(facilityId).card_profiles;
        const index = profiles.findIndex(item => item.id === profile.id);
        if(index !== -1) {
          profiles.splice(index, 1);
        }
      } catch (error) {
        console.error('deleteCardProfile failed', error);
        let message = t('api.deleteBadgeFormatFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    async loadReleaseNotes() {
      try {
        const response = await axios.get('/conf/release_notes');
        return response.data;
      } catch (error) {
        console.error('loadReleaseNotes', error);
        return [];
      }
    },

    openSupportPanel() {
      this.supportPanelRequested = true;
    },

    async loadSupportRequests() {
      try {
        const response = await axios.get('/conf/support/requests');
        this.supportUnread = response.data.reduce((sum, r) => sum + (r.unread || 0), 0);
        return response.data;
      } catch (error) {
        console.error('loadSupportRequests', error);
        return [];
      }
    },

    async createSupportRequest(kind, subject, message) {
      try {
        const response = await axios.post('/conf/support/requests', { kind, subject, message });
        return response.data;
      } catch (error) {
        console.error('createSupportRequest', error);
        let errorMessage = t('api.unexpectedError');
        if (error.response) errorMessage += ` (${error.response.status})`;
        throw errorMessage;
      }
    },

    async loadSupportMessages(requestId) {
      try {
        const response = await axios.get(`/conf/support/requests/${requestId}/messages`);
        return response.data;
      } catch (error) {
        console.error('loadSupportMessages', error);
        return [];
      }
    },

    async sendSupportMessage(requestId, body) {
      try {
        const response = await axios.post(`/conf/support/requests/${requestId}/messages`, { body });
        return response.data;
      } catch (error) {
        console.error('sendSupportMessage', error);
        let message = t('api.unexpectedError');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    // Load facility alerts
    async loadAlerts(facilityId) {
      try {
        const response = await axios.get(`/conf/facilities/${facilityId}/alerts`);
        return ApiClient.convertToType(response.data, [Alert]);
      } catch (error) {
        console.error('Alerts loading failed', error);
        let message = t('api.loadAlertsFailed');
        if (error.response) message += ` (${error.response.status})`
        throw message;
      }
    },

    async ackAlert(facilityId, alertId) {
      try {
        await axios.post(`/conf/facilities/${facilityId}/alerts/${alertId}/ack`);
        const facility = this.getFacility(facilityId);
        if (facility && facility.alert_counter > 0) {
          facility.alert_counter--;
        }
      } catch (error) {
        console.error('Alert ack failed', error);
        let message = t('api.ackAlertFailed');
        if (error.response) message += ` (${error.response.status})`
        throw message;
      }
    },

    async ackAllAlerts(facilityId) {
      try {
        await axios.post(`/conf/facilities/${facilityId}/alerts/all/ack`);
        const facility = this.getFacility(facilityId);
        if (facility) {
          facility.alert_counter = 0;
        }
      } catch (error) {
        console.error('Alerts ack failed', error);
        let message = t('api.ackAllAlertsFailed');
        if (error.response) message += ` (${error.response.status})`
        throw message;
      }
    },

    getFacility(facilityId) {
      return this.facilities.find(facility => facility.id === facilityId);
    },

    getFacilityHubs(facilityId, excludedType) {
      const facility = this.getFacility(facilityId);
      if (!facility) return [];
      if (!excludedType) return facility.hubs;

      return facility.hubs.filter(hub => hub.equipement_type !== excludedType);
    },

    getHub(facilityId, hubId) {
      const facility = this.getFacility(facilityId);
      return facility.hubs.find(hub => hub.id === hubId);
    },

    getHubs(facilityId) {
      const facility = this.getFacility(facilityId);
      return facility.hubs;
    },

    getExternalAlarms(facilityId) {
      const facility = this.getFacility(facilityId);
      return facility.external_alarms;
    },

    getScenario(hub, id) {
      const scenario = hub.scenarios.find(s => s.id === id);
      if (scenario) {
        return scenario.clone();
      } else {
        throw t('api.scenarioNotFound')
      }
    },

    async updateInstaller(updatedInstaller) {
      try {
        const response = await axios.put('/conf/installers/me', updatedInstaller);
        this.installer.update(updatedInstaller, response.data.picture);
        return 'ok';
      } catch (error) {
        console.error('updateInstaller', error);
        if (error.response) {
          switch (error.response.status) {
            case 400:
              // The approval on a phone needs a phone enrolled from the app.
              if (Array.isArray(error.response.data) && error.response.data.includes('mfa')) {
                throw t('account.mfaPushNoPhone');
              }
              throw `${t('api.unexpectedError')} (${error.response.status})`;
            case 409:
              throw t('api.emailAlreadyUsed');
            case 428:
              this.mfa_info = ApiClient.convertToType(error.response.data.mfa_info, MfaInfo);
              return 'need_mfa';
            default:
              throw `${t('api.unexpectedError')} (${error.response.status})`;
          }
        }
        return error;
      }
    },

    // True when a client already uses this administrator email : creating a facility
    // with it attaches the facility to that client (multi-site) instead of creating a new one
    async isAdminEmailInUse(email) {
      try {
        const response = await axios.get('/conf/facilities/admin_email/in_use', { params: { email } });
        return response.status === 200 && response.data === true;
      } catch (error) {
        console.error("isAdminEmailInUse failed", error.response?.status, error.response?.data);
        return false;
      }
    },

    async createInstallation(installation) {
      try {
        const response = await axios.post('/conf/facilities', installation);
        const newFacility = Facility.constructFromObject(response.data);
        this.facilities.push(newFacility);
        
        const authStore = useAuthStore();
        await authStore.refreshToken();
        
        return newFacility;
      } catch (error) {
        console.error("createInstallation failed", error.response?.status, error.response?.data);
        let message = t('api.createInstallationFailed');
        if (error.response) {
          switch (error.response.status) {
            case 409:
              throw t('api.nameAlreadyUsed');
            case 422:
              throw `${t('api.invalidForm')} : ${JSON.stringify(error.response.data)}`;
            default:
              throw `${message} (${error.response.status}) : ${JSON.stringify(error.response.data)}`;
          }
        }
        throw message;
      }
    },

    async updateFacility(facility, updatedFacility) {
      try {
        const response = await axios.put(`/conf/facilities/${facility.id}`, updatedFacility);
        facility.update(updatedFacility, response.data.picture);
      } catch (error) {
        let message = t('api.updateInstallationFailed');
        if (error.response) {
          switch (error.response.status) {
            case 409:
              throw t('api.nameAlreadyUsed');
            default:
              message += ` (${error.response.status})`;
          }
        }
        throw message;
      }
    },

    async deleteFacility(facility_id) {
      const facility = this.facilities.find(f => f.id == facility_id);
      if (facility === undefined) {
        throw t('api.installationNotFound');
      }
      if (!facility.is_leasing && facility.validated && facility.admin_email != this.installer.email) {
        throw t('api.installationAlreadyValidated');
      }
      try {
        await axios.delete(`/conf/facilities/${facility_id}`);
        this.facilities = this.facilities.filter(f => f.id != facility_id);
      } catch (error) {
        console.error("Delete facility failed", error);
        throw t('api.deleteInstallationFailed');
      }
    },

    async updateHub(facilityId, hub, updatedHub) {
      try {
        await axios.put(`/conf/facilities/${facilityId}/components/${hub.id}`, updatedHub);
      } catch (error) {
        console.error("updateHub failed", error);
        let message = t('api.updateHubFailed');
        if (error.response) message += ` (${error.response.status})`
        throw message;
      }
    },

    // Send the AJAX account to the LINK
    async configureAjax(facilityId, hub, config) {
      try {
        await axios.post(`/conf/facilities/${facilityId}/components/${hub.serial_number}/ajax/configure`, config);
      } catch (error) {
        console.error("configureAjax failed", error);
        throw integrationErrorMessage(error);
      }
    },

    // Send the SPC panel configuration to the LINK and keep it for the next edition
    async configureSpc(facilityId, hub, config) {
      try {
        await axios.post(`/conf/facilities/${facilityId}/components/${hub.serial_number}/spc/configure`, config);
        hub.spc_config = config;
      } catch (error) {
        console.error("configureSpc failed", error);
        throw integrationErrorMessage(error);
      }
    },

    // NVRs and cameras declared on a LINK, kept on the hub for the device page
    async loadVideoDevices(facilityId, hub) {
      const base = videoBasePath(facilityId, hub);
      const results = await Promise.allSettled([axios.get(`${base}/nvrs`), axios.get(`${base}/cameras`)]);
      for (const result of results) {
        if (result.status === 'rejected') {
          console.error("loadVideoDevices failed", result.reason);
          const response = result.reason.response;
          throw t('video.loadFailed', { code: response ? response.status : -1 });
        }
      }
      const [nvrs, cameras] = results.map(result => Array.isArray(result.value.data) ? result.value.data.filter(d => VideoDevice.isReadable(d)) : []);
      hub.nvrs = nvrs.map(d => NVR.constructFromObject(d));
      hub.cameras = cameras.map(d => Camera.constructFromObject(d));
    },

    // Probe of a device through the LINK: identity, NVR or not, channels. Writes nothing.
    async probeVideoDevice(facilityId, hub, device) {
      try {
        const response = await axios.post(`${videoBasePath(facilityId, hub)}/cameras/probe`, device.toProbeJSON(), { timeout: VIDEO_PROBE_TIMEOUT });
        return ProbeResult.constructFromObject(response.data);
      } catch (error) {
        console.error("probeVideoDevice failed", error);
        if (!error.response) throw t('video.probeFailed');
        switch (error.response.status) {
          case 404: {
            const data = error.response.data;
            const reason = data && typeof data === 'object' ? data.reason : null;
            throw reason != null && String(reason) !== '' ? t('video.probeFailedReason', { reason: String(reason) }) : t('video.probeFailed');
          }
          case 408:
            throw t('video.probeFailedTimeout');
          case 412:
            throw t('video.probeFailedLinkNotConnected');
          default:
            throw t('video.probeFailedCode', { code: error.response.status });
        }
      }
    },

    // Creates (POST) or updates (PUT) an NVR or a camera, returns the id sent back by the API
    async saveVideoDevice(facilityId, hub, device) {
      const path = `${videoBasePath(facilityId, hub)}/${device.apiPath}`;
      try {
        const response = device.isNew ? await axios.post(path, device.toJSON()) : await axios.put(`${path}/${device.id}`, device.toJSON());
        return response.data && response.data.id != null ? response.data.id : null;
      } catch (error) {
        console.error("saveVideoDevice failed", error);
        if (error.response && error.response.status === 402) throw t('video.licenseNeeded');
        if (error.response && error.response.status === 409) throw t('video.conflictIp');
        throw integrationErrorMessage(error);
      }
    },

    // Declares a channel of a new NVR as a camera, false when it could not be created
    async declareCamera(facilityId, hub, camera) {
      try {
        await axios.post(`${videoBasePath(facilityId, hub)}/cameras`, camera.toJSON());
        return true;
      } catch (error) {
        console.error("declareCamera failed", error);
        return false;
      }
    },

    // Deleting an NVR also deletes its cameras
    async deleteVideoDevice(facilityId, hub, device) {
      try {
        await axios.delete(`${videoBasePath(facilityId, hub)}/${device.apiPath}/${device.id}`);
      } catch (error) {
        console.error("deleteVideoDevice failed", error);
        if (error.response && error.response.status === 412) throw t('api.linkNotConnected');
        let message = t('common.deleteFailure');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    // ONVIF WS-Discovery run on the LINK network, declared devices included. Writes nothing.
    async discoverOnvifDevices(facilityId, hub) {
      try {
        const response = await axios.post(`${videoBasePath(facilityId, hub)}/cameras/discover`, { timeout_secs: 5 }, { timeout: VIDEO_DISCOVER_TIMEOUT });
        const cameras = response.data && Array.isArray(response.data.cameras) ? response.data.cameras : [];
        return cameras.map(c => DiscoveredCamera.constructFromObject(c)).filter(Boolean);
      } catch (error) {
        console.error("discoverOnvifDevices failed", error);
        if (!error.response) throw t('video.discoverFailed');
        switch (error.response.status) {
          case 408:
            throw t('video.discoverFailedTimeout');
          case 412:
            throw t('video.discoverFailedLinkNotConnected');
          default:
            throw t('video.discoverFailedCode', { code: error.response.status });
        }
      }
    },

    // License types a LINK can activate, loaded once as in the app
    async loadAvailableLicenses() {
      if (this.availableLicenses) return this.availableLicenses;
      try {
        const response = await axios.get('/conf/licenses/integrated/limits');
        this.availableLicenses = ApiClient.convertToType(response.data, [License]) || [];
        return this.availableLicenses;
      } catch (error) {
        console.error("loadAvailableLicenses failed", error);
        throw t('componentEditLink.licensesLoadFailed');
      }
    },

    // Limits (types and maximums) of a license product from its key, before it is attached to a LINK
    async getLicenseLimits(uniqueId) {
      try {
        const response = await axios.get(`/conf/licenses/${encodeURIComponent(uniqueId)}/limits`);
        return ApiClient.convertToType(response.data, [License]) || [];
      } catch (error) {
        console.error("getLicenseLimits failed", error);
        switch (error.response ? error.response.status : null) {
          case 404: throw t('licenseModal.keyNotFound');
          default: throw t('licenseModal.checkFailed', { code: error.response ? error.response.status : -1 });
        }
      }
    },

    // Activates a license on a LINK from its key
    async addLinkLicense(facilityId, hub, uniqueId, name) {
      try {
        const response = await axios.post(`/conf/facilities/${facilityId}/components/${hub.serial_number}/licenses`, { unique_id: uniqueId, name: name });
        const license = License.constructFromObject(response.data);
        hub.licenses.push(license);
        return license;
      } catch (error) {
        console.error("addLinkLicense failed", error);
        switch (error.response ? error.response.status : null) {
          case 404: throw t('licenseModal.addNotFound');
          case 409: throw t('licenseModal.addAlreadyActive');
          case 412: throw t('licenseModal.addLinkNotConnected');
          default: throw t('licenseModal.addFailed', { code: error.response ? error.response.status : -1 });
        }
      }
    },

    // Changes the type of a license of a LINK
    async updateLinkLicense(facilityId, hub, license, name) {
      try {
        const response = await axios.put(`/conf/facilities/${facilityId}/components/${hub.serial_number}/licenses/${license.id}`, { name: name });
        const updated = License.constructFromObject(response.data);
        const index = hub.licenses.findIndex(l => l.id === updated.id);
        if (index !== -1) hub.licenses[index] = updated;
        return updated;
      } catch (error) {
        console.error("updateLinkLicense failed", error);
        switch (error.response ? error.response.status : null) {
          case 404: throw t('licenseModal.editNotFound');
          case 412: throw t('licenseModal.editLinkNotConnected');
          default: throw t('licenseModal.editFailed', { code: error.response ? error.response.status : -1 });
        }
      }
    },

    async deleteLinkLicense(facilityId, hub, license) {
      try {
        await axios.delete(`/conf/facilities/${facilityId}/components/${hub.serial_number}/licenses/${license.id}`);
        hub.licenses = hub.licenses.filter(l => l.id !== license.id);
      } catch (error) {
        console.error("deleteLinkLicense failed", error);
        switch (error.response ? error.response.status : null) {
          case 404: throw t('componentEditLink.licenseDeleteNotFound');
          case 412: throw t('componentEditLink.licenseDeleteLinkNotConnected');
          default: throw t('componentEditLink.licenseDeleteFailed', { code: error.response ? error.response.status : -1 });
        }
      }
    },

    // Licenses activated on the installation itself, read with the installation as in the app
    async loadFacilityLicenses(facilityId) {
      try {
        const response = await axios.get(`/conf/facilities/${facilityId}`);
        return ApiClient.convertToType(response.data.licenses, [License]) || [];
      } catch (error) {
        console.error("loadFacilityLicenses failed", error);
        let message = t('facilityLicenseModal.loadFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    // Same messages as for a LINK's license, as in the app
    async deleteFacilityLicense(facilityId, license) {
      try {
        await axios.delete(`/conf/facilities/${facilityId}/licenses/${license.id}`);
      } catch (error) {
        console.error("deleteFacilityLicense failed", error);
        switch (error.response ? error.response.status : null) {
          case 404: throw t('componentEditLink.licenseDeleteNotFound');
          case 412: throw t('componentEditLink.licenseDeleteLinkNotConnected');
          default: throw t('componentEditLink.licenseDeleteFailed', { code: error.response ? error.response.status : -1 });
        }
      }
    },

    // Activates a license on the installation itself from its key alone, as the app does: no type
    // is chosen (name null), the license comes back with the type of its key. Same messages as a LINK's.
    async addFacilityLicense(facilityId, uniqueId) {
      try {
        const response = await axios.post(`/conf/facilities/${facilityId}/licenses`, { unique_id: uniqueId, name: null });
        return License.constructFromObject(response.data);
      } catch (error) {
        console.error("addFacilityLicense failed", error);
        switch (error.response ? error.response.status : null) {
          case 404: throw t('licenseModal.addNotFound');
          case 409: throw t('licenseModal.addAlreadyActive');
          case 412: throw t('licenseModal.addLinkNotConnected');
          default: throw t('licenseModal.addFailed', { code: error.response ? error.response.status : -1 });
        }
      }
    },

    // Imports the Smart Intego configuration file (CSV encoded in base64) on a LINK
    async importSmartIntego(facilityId, hub, csvBase64) {
      try {
        await axios.post(`${componentPath(facilityId, hub)}/smart_intego/configure`, { csv_base64: csvBase64 }, { timeout: MECA_TIMEOUT });
      } catch (error) {
        console.error("importSmartIntego failed", error);
        throw integrationErrorMessage(error);
      }
    },

    // Pairs the UZ product the installer presents the SERVICE badge to
    async pairUZ(facilityId, hub, pairing) {
      try {
        await axios.post(`${componentPath(facilityId, hub)}/uz/pair`, pairing, { timeout: MECA_TIMEOUT });
      } catch (error) {
        console.error("pairUZ failed", error);
        switch (error.response ? error.response.status : null) {
          case null:
          case 404: throw t('meca.uzPairingFailed');
          case 412: throw t('meca.uzPairingFailedLinkNotConnected');
          default: throw t('meca.uzPairingFailedCode', { code: error.response.status });
        }
      }
    },

    // Locks announced by an Aperio hub, the declared ones included
    async discoverAperioLocks(facilityId, hub, gatewayIp) {
      try {
        const response = await axios.post(`${componentPath(facilityId, hub)}/aperio/discover`, { gateway_ip: gatewayIp }, { timeout: APERIO_DISCOVER_TIMEOUT });
        const locks = response.data && Array.isArray(response.data.locks) ? response.data.locks : [];
        return locks
          .filter(lock => lock && typeof lock.device_group_id === 'string')
          .map(lock => ({ device_group_id: lock.device_group_id, name: lock.name ?? '', mac: lock.mac ?? null }));
      } catch (error) {
        console.error("discoverAperioLocks failed", error);
        switch (error.response ? error.response.status : null) {
          case null: throw t('meca.aperioDiscoverFailed');
          case 404: throw t('meca.aperioDiscoverFailedHubNotFound');
          case 408: throw t('meca.aperioDiscoverFailedTimeout');
          case 412: throw t('meca.aperioDiscoverFailedLinkNotConnected');
          default: throw t('meca.aperioDiscoverFailedCode', { code: error.response.status });
        }
      }
    },

    async declareAperioLock(facilityId, hub, declaration) {
      try {
        await axios.post(`${componentPath(facilityId, hub)}/aperio/declare`, declaration, { timeout: MECA_TIMEOUT });
      } catch (error) {
        console.error("declareAperioLock failed", error);
        switch (error.response ? error.response.status : null) {
          case null: throw t('meca.aperioDeclareFailed');
          case 409: throw t('meca.aperioDeclareFailedAlreadyDeclared');
          case 412: throw t('meca.aperioDeclareFailedLinkNotConnected');
          default: throw t('meca.aperioDeclareFailedCode', { code: error.response.status });
        }
      }
    },

    // Removes a UZ product from the LINK
    async unpairUZReader(facilityId, hub, reader) {
      try {
        await axios.post(`${componentPath(facilityId, hub)}/uz/${reader.position}/unpair`, null, { timeout: MECA_TIMEOUT });
        hub.readers = hub.readers.filter(r => r.position !== reader.position);
      } catch (error) {
        console.error("unpairUZReader failed", error);
        switch (error.response ? error.response.status : null) {
          case null: throw t('meca.deleteFailed');
          case 404: throw t('meca.unpairFailed');
          case 412: throw t('meca.unpairFailedLinkNotConnected');
          default: throw t('meca.unpairFailedCode', { code: error.response.status });
        }
      }
    },

    async deleteAperioReader(facilityId, hub, reader) {
      try {
        await axios.delete(`${componentPath(facilityId, hub)}/aperio/${reader.position}`, { timeout: MECA_TIMEOUT });
        hub.readers = hub.readers.filter(r => r.position !== reader.position);
      } catch (error) {
        console.error("deleteAperioReader failed", error);
        switch (error.response ? error.response.status : null) {
          case null: throw t('meca.deleteFailed');
          case 412: throw t('meca.deleteFailedLinkNotConnected');
          default: throw t('meca.deleteFailedCode', { code: error.response.status });
        }
      }
    },

    async updateReader(facilityId, hubId, reader, updatedReader) {
      try {
        await axios.put(`/conf/facilities/${facilityId}/components/${hubId}/readers/${reader.position}`, updatedReader);
        reader.update(updatedReader);
        return true;
      } catch (error) {
        console.error("updateReader failed", error);
        return false;
      }
    },

    async updateScenario(facilityId, hub, updatedScenario) {
      try {
        if (updatedScenario.id === 0) {
          // New scenario
          const response = await axios.post(`/conf/facilities/${facilityId}/components/${hub.id}/scenarios`, updatedScenario);
          const scenario = ApiClient.convertToType(response.data, Scenario);
          updatedScenario.id = scenario.id;
          hub.addScenario(scenario);
        } else {
          // Existing scenario
          const response = await axios.put(`/conf/facilities/${facilityId}/components/${hub.id}/scenarios/${updatedScenario.id}`, updatedScenario);
          const scenario = ApiClient.convertToType(response.data, Scenario);
          hub.updateScenario(scenario);
        }
        return true;
      } catch (error) {
        console.error("updateScenario failed", error);
        let message = t('api.updateScenarioFailed');
        if (error.response) message += ` (${error.response.status})`
        throw message;
      }
    },

    async deleteHub(facilityId, hub, reason) {
      try {
        await axios.delete(`/conf/facilities/${facilityId}/components/${hub.serial_number}`, { data: reason });
        const facility = this.getFacility(facilityId);
        const index = facility.hubs.findIndex(h => h.id === hub.id);
        if (index !== -1) {
          facility.hubs.splice(index, 1);
          facility.updateComponents();
        }
      } catch (error) {
        console.error("deleteHub failed", error);
        if (error.response && error.response.status === 409) {
          throw t('api.deleteHubConflict');
        }
        let message = t('api.deleteHubFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    // Asks the server to order a firmware update of a hub / link connected through MQTT
    // (same endpoint as the Haxo Setup app, without its BLE fallback).
    // Resolves with 'ordered' (the device updates and reboots, the result is read with
    // readFirmwareUpdateResult) or 'uptodate'; throws a translated message otherwise.
    async orderFirmwareUpdate(facilityId, hub) {
      try {
        const response = await axios.post(`${componentPath(facilityId, hub)}/update`);
        console.log("firmware update order answered", response.status, response.data);
        return 'ordered';
      } catch (error) {
        console.error("orderFirmwareUpdate failed", error);
        const status = error.response?.status;
        switch (status) {
          case 409:
            return 'uptodate';
          case 412:
            throw t('firmwareUpdate.errorNotConnected', { device: hub.productName });
          case 404:
            throw t('firmwareUpdate.errorNotFound');
          case 503:
            throw t('firmwareUpdate.errorNoFirmware');
          default:
            throw t('firmwareUpdate.errorServer', { code: status ?? error.code ?? '' });
        }
      }
    },

    // Reads the result the device reports to the server once it is back from the update.
    // The result is consumed on read. Resolves with the result ({ status, version, code, detail }),
    // or null while there is none yet (or on a transient error); throws when the session is rejected.
    async readFirmwareUpdateResult(hub) {
      try {
        const response = await axios.get(`/conf/components/${hub.serial_number}/update_result`);
        console.log("firmware update result answered", response.status, response.data);
        return response.data;
      } catch (error) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          throw t('firmwareUpdate.errorServer', { code: status });
        }
        // 404: no result yet, the device is still updating / rebooting; other errors are transient
        if (status !== 404) console.error("readFirmwareUpdateResult failed", error);
        return null;
      }
    },

    async deleteExtension(facilityId, hub, module, reason) {
      try {
        await axios.delete(`/conf/facilities/${facilityId}/components/${hub.serial_number}/extensions/${module.serial_number}`, { data: reason });
        const index = hub.extensions.findIndex(e => e.id === module.id);
        if (index !== -1) {
          hub.extensions.splice(index, 1);
          this.getFacility(facilityId).updateComponents();
        }
      } catch (error) {
        console.error("deleteExtension failed", error);
        let message = t('api.deleteExtensionFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    async deleteScenario(facilityId, hub, scenarioId) {
      try {
        const index = hub.scenarios.findIndex(scenario => scenario.id == scenarioId);
        if (index == -1) return false;
        await axios.delete(`/conf/facilities/${facilityId}/components/${hub.id}/scenarios/${scenarioId}`);
        hub.scenarios.splice(index, 1);
        return true;
      } catch (error) {
        console.error("deleteScenario failed", error);
        return false;
      }
    },

    async configureFacility(uuid, name, first_name, password, mobile_phone_number, cguAccepted) {
      try {
        const configuration = {
          name: name,
          first_name: first_name,
          password: password,
          mobile_phone_number: mobile_phone_number,
          mfa: 'none',
          cgu_accepted: cguAccepted,
        };
        const response = await axios.post(`/conf/facilities/configure/${uuid}`, configuration);
        return response.data?.domain_prefix;
      } catch (error) {
        console.error('configureFacility', error);
        if (error.response) {
          throw `${t('api.unexpectedError')} (${error.response.status})`;
        }
        throw error;
      }
    },

    async resendAdminEmail(facility_id, email) {
      try {
        await axios.post('/conf/facilities/admin_resend', { facility_id: facility_id, email: email });
      } catch (error) {
        console.error("resendAdminEmail failed", error);
        if (error.response) {
          switch (error.response.status) {
            case 409:
              throw t('api.validationEmailAlreadyUsed');
            default:
              throw `${t('api.sendMailFailed')} (${error.response.status})`;
          }
        }
        throw error;
      }
    },

    async checkUUID(uuid, type) {
      try {
        const response = await axios.get(`/conf/uuid/verify?uuid=${uuid}&type=${type}`);
        return response.data;
      } catch (error) {
        console.error("checkUUID", error);
        throw t('api.invalidLink');
      }
    },

    async loadMembers() {
      try {
        const response = await axios.get('/conf/installers/me/installers');
        return ApiClient.convertToType(response.data, [Member]);
      } catch (error) {
        console.error("loadTeamMembers failed", error);
        if (error.response) {
          throw `${t('api.loadMembersFailed')} (${error.response.status})`;
        }
        throw error;
      }
    },

    async loadMember(id) {
      try {
        const response = await axios.get(`/conf/installers/me/installers/${id}`);
        return ApiClient.convertToType(response.data, Member);
      } catch (error) {
        console.error("loadTeamMember failed", error);
        if (error.response) {
          throw `${t('api.loadInfoFailed')} (${error.response.status})`;
        }
        throw error;
      }
    },

    async inviteMember(email) {
      try {
        await axios.post('/conf/installers/me/invite', { email: email });
      } catch (error) {
        console.error("inviteMember failed", error);
        if (error.response) {
          switch (error.response.status) {
            case 404:
              throw t('api.inviteInstallerNotFound');
            case 409:
              throw t('api.inviteAlreadyInTeam');
            case 412:
              throw t('api.inviteNotValidated');
            default:
              throw `${t('api.inviteFailed')} (${error.response.status})`;
          }
        }
        throw error;
      }
    },

    async updateMember(member) {
      try {
        if (member.id === 0) {
          await axios.post('/conf/installers/me/installers', member);
        } else {
          await axios.put(`/conf/installers/me/installers/${member.id}`, member);
        }
      } catch (error) {
        console.error("updateMember failed", error);
        if (error.response) {
          switch (error.response.status) {
            case 409:
              throw t('api.createEmailUsed');
            default:
              throw `${t('api.saveFailed')} (${error.response.status})`;
          }
        }
        throw error;
      }
    },

    async removeMember(id) {
      try {
        await axios.delete(`/conf/installers/me/installers/${id}`);
      } catch (error) {
        console.error("removeTeamMember failed", error);
        if (error.response) {
          throw `${t('api.deleteInstallerFailed')} (${error.response.status})`;
        }
        throw error;
      }
    },

    async acceptInvitation(uuid) {
      try {
        await axios.post(`/conf/invitations/${uuid}/accept`);
      } catch (error) {
        console.error("acceptInvitation failed", error);
        if (error.response) {
          throw `${t('api.acceptInvitationFailed')} (${error.response.status})`;
        }
        throw error;
      }
    },

    async declineInvitation(uuid) {
      try {
        await axios.post(`/conf/invitations/${uuid}/decline`);
      } catch (error) {
        console.error("declineInvitation failed", error);
        if (error.response) {
          throw `${t('api.declineInvitationFailed')} (${error.response.status})`;
        }
        throw error;
      }
    },

    async validateInstaller(uuid, password, mfa, totpSecret, totpCode, code, cguAccepted) {
      try {
        const configuration = {
          password: password,
          mfa: mfa,
          cgu_accepted: cguAccepted,
        };
        if (mfa == 'totp') {
          configuration.totp = { secret: totpSecret, code: totpCode };
        }
        if (code) {
          configuration.code = code;
        }
        await axios.post(`/conf/installers/accept_with_password/${uuid}`, configuration);
        return 'ok';
      } catch (error) {
        console.error('validateInstaller failed', error);
        let message = t('api.validateAccountFailed');
        if (error.response) {
          switch (error.response.status) {
            case 428:
              this.mfa_info = ApiClient.convertToType(error.response.data.mfa_info, MfaInfo);
              return 'need_mfa';
            default:
              message += ` (${error.response.status})`;
              break;
          }
        }
        throw message;
      }
    },

    async loadInterventions() {
      try {
        const response = await axios.get('/conf/installers/me/interventions');
        return ApiClient.convertToType(response.data, [Intervention]);
      } catch (error) {
        console.error("loadInterventions failed", error);
        let message = t('api.loadInterventionsFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    async createIntervention(payload) {
      try {
        const response = await axios.post('/conf/installers/me/interventions', payload);
        return ApiClient.convertToType(response.data, Intervention);
      } catch (error) {
        console.error("createIntervention failed", error);
        let message = t('api.createInterventionFailed');
        if (error.response) {
          switch(error.response.status) {
            case 404:
              message += t('api.installerNotFound');
              break;
            default:
              message += ` (${error.response.status})`;
              break;
          }
          throw message;
        }
      }
    },

    async updateIntervention(intervention) {
      try {
        const response = await axios.put(`/conf/installers/me/interventions/${intervention.id}`, intervention);
        return ApiClient.convertToType(response.data, Intervention);
      } catch (error) {
        console.error("updateIntervention failed", error);
        let message = t('api.updateInterventionFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    async deleteIntervention(id) {
      try {
        await axios.delete(`/conf/installers/me/interventions/${id}`);
      } catch (error) {
        console.error("deleteIntervention failed", error);
        let message = t('api.deleteInterventionFailed');
        if (error.response) message += ` (${error.response.status})`;
        throw message;
      }
    },

    loadLPRs(facilityId, currentLPRId) {
      try {
        const hubs = this.getHubs(facilityId);
        const allLPRs = hubs.flatMap(h => h.lprs);

        if (allLPRs.length === 0) {
          return null;
        }

        const usedByOthers = new Set(
          hubs.flatMap(h => h.readers)
            .filter(r => r.lpr_source_id > 0)
            .map(r => r.lpr_source_id)
        );

        return allLPRs
          .filter(
            lpr =>
              lpr.id === currentLPRId ||
              !usedByOthers.has(lpr.id)
          )
          .map(lpr => ({
            id: lpr.id,
            name: lpr.name
          }));
      } catch (e) {
        console.error(e);
        return null;
      }
    },
  },
  persist: {
    storage: sessionStorage,
  }
});
